/**
 * i18n Configuration
 * Initializes react-i18next with bundled locale files and OS language detection.
 *
 * UI language is driven by UILanguagePreference ('source' | 'target'),
 * resolved to an actual locale via fallback chains.
 * See: docs/specs/mobile/locale-system.mobile.md (LOCALE-003)
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import * as Localization from 'expo-localization';
import enUS from './locales/en-US.json';
import es419 from './locales/es-419.json';
import enGB from './locales/en-GB.json';
import esMX from './locales/es-MX.json';
import esES from './locales/es-ES.json';
import { createLogger } from '../utils/logger';
import {
  getLocalPreferences,
  isLocalPreferencesLoaded,
  loadLocalPreferences,
  updateLocalPreferences,
} from '../services/localPreferences';
import {
  getBaseLanguage,
  getDefaultVariant,
  getFallbackChain,
  buildI18nFallbacks,
  DEFAULT_UI_LANGUAGE,
} from '../types/locale';
import type { UILanguagePreference } from '../types/locale';
import { fallbackSourceLocale, findCourseForLocale } from '../store/courseStore';

const log = createLogger('i18n');

// ─── Supported i18n resource keys ────────────────────────────────
// Checks bundled translation resources at runtime.

function hasI18nResource(locale: string): boolean {
  return i18n.hasResourceBundle(locale, 'translation');
}

// ─── UI Language Preference ──────────────────────────────────────

export type { UILanguagePreference };

/** Supported UI language preferences for the toggle */
export const UI_LANGUAGE_PREFERENCES: readonly UILanguagePreference[] = ['source', 'target'];

// ─── Display Name Helpers ────────────────────────────────────────

/**
 * Get a language's display name in a specific locale.
 * Uses Intl.DisplayNames (OS-provided) with fallback to the locale file's native.language key.
 */
export function getLanguageDisplayName(languageCode: string, inLocale: string): string {
  try {
    const displayNames = new Intl.DisplayNames([inLocale], { type: 'language' });
    const name = displayNames.of(languageCode);
    if (name && name !== languageCode) {
      return name.charAt(0).toUpperCase() + name.slice(1);
    }
  } catch {
    // Intl.DisplayNames not supported — fall through
  }

  try {
    const t = i18n.getFixedT(languageCode);
    const nativeName = t('native.language');
    if (nativeName && nativeName !== 'native.language') return nativeName;
  } catch {
    // No locale file for this language
  }

  return languageCode;
}

/**
 * Get a country's display name in a specific locale.
 */
export function getCountryDisplayName(countryCode: string, inLocale: string): string {
  try {
    const displayNames = new Intl.DisplayNames([inLocale], { type: 'region' });
    const name = displayNames.of(countryCode.toUpperCase());
    if (name && name !== countryCode) return name;
  } catch {
    // Intl.DisplayNames not supported
  }
  return countryCode;
}

// ─── Locale Resolution ───────────────────────────────────────────

/**
 * Resolve a locale to the best available i18n resource.
 * Uses the same fallback chain as i18next (locale → default variant → DEFAULT_UI_LANGUAGE).
 */
function resolveI18nLocale(locale: string): string {
  if (hasI18nResource(locale)) return locale;
  for (const fallback of getFallbackChain(locale)) {
    if (hasI18nResource(fallback)) return fallback;
  }
  return DEFAULT_UI_LANGUAGE;
}

/**
 * Resolve UI language from preference + user's locales.
 * Called at runtime to determine which i18n resource to use.
 */
export function resolveUILanguage(
  pref: UILanguagePreference,
  sourceLocale: string,
  targetLocale: string,
): string {
  if (pref === 'source') {
    return resolveI18nLocale(sourceLocale);
  }
  // Target preference: try target first
  const targetResolved = resolveI18nLocale(targetLocale);
  // If it resolved to something other than the final fallback, use it.
  // If it fell through to DEFAULT_UI_LANGUAGE, check if that's actually
  // the target's default variant (genuine match) vs a fallthrough.
  const targetDefault = getDefaultVariant(getBaseLanguage(targetLocale));
  if (targetResolved !== DEFAULT_UI_LANGUAGE || targetDefault === DEFAULT_UI_LANGUAGE) {
    return targetResolved;
  }
  // Target fell through to final fallback — try source chain before giving up
  return resolveI18nLocale(sourceLocale);
}

/**
 * Sync i18n language when source/target locale changes.
 * Re-resolves the UI language based on the current preference.
 */
export async function syncI18nVariant(sourceLocale: string): Promise<void> {
  // Load local preferences BEFORE reading the preference — a stored explicit
  // choice must win over OS detection, and the effective preference can't
  // see it until the AsyncStorage read lands.
  if (!isLocalPreferencesLoaded()) {
    await loadLocalPreferences();
  }
  const pref = getUILanguagePreference();
  // Only re-resolve if the preference is 'source' (target changes don't affect source pref)
  if (pref !== 'source') return;

  const { targetLocale } = getActiveLocales();

  const i18nLang = resolveUILanguage(pref, sourceLocale, targetLocale);
  if (i18n.language !== i18nLang) {
    await i18n.changeLanguage(i18nLang);
    log.info('i18n variant synced', { sourceLocale, i18nLang });
  }
}

// ─── Active locale resolution (lazy store access) ───────────────

/** Read current source + target locales from userPreferencesStore, with courseStore fallback. */
function getActiveLocales(): { sourceLocale: string; targetLocale: string } {
  let sourceLocale = fallbackSourceLocale();
  let targetLocale = findCourseForLocale(sourceLocale)?.defaultTargetLocale ?? '';
  try {
    const { useUserPreferencesStore } = require('../store/userPreferencesStore');
    const state = useUserPreferencesStore.getState();
    sourceLocale = state.sourceLocale;
    targetLocale = state.targetLocale;
  } catch {
    // Store not ready
  }
  return { sourceLocale, targetLocale };
}

// ─── UI Language Preference Persistence ──────────────────────────

/**
 * Derive a UI language preference from the OS language (LOCALE-003-01).
 * Compares the OS language's base against the active course's two sides:
 * the OS speaks the source language → 'source'; the target language →
 * 'target'. Returns undefined when the OS language matches neither side
 * (callers decide the fallback — initI18n uses DEFAULT_UI_LANGUAGE per
 * LOCALE-003-02) or when OS locales are unavailable.
 */
export function detectUILanguagePreferenceFromOS(
  sourceLocale: string,
  targetLocale: string,
): UILanguagePreference | undefined {
  try {
    const osBase = Localization.getLocales()[0]?.languageCode;
    if (osBase) {
      if (osBase === getBaseLanguage(sourceLocale)) return 'source';
      if (osBase === getBaseLanguage(targetLocale)) return 'target';
    }
  } catch {
    // Localization unavailable — no OS signal
  }
  return undefined;
}

/**
 * Get the effective UI language preference.
 * A stored 'source'/'target' is an explicit user choice and always wins.
 * When unset ('' — the default; detection results are never written back),
 * the preference follows the OS language so every reader (settings toggle,
 * syncI18nVariant) agrees with what initI18n resolved. Falls back to
 * 'target' when the OS language matches neither course side.
 */
export function getUILanguagePreference(): UILanguagePreference {
  const stored = getLocalPreferences().uiLanguage;
  if (stored === 'source' || stored === 'target') return stored;
  const { sourceLocale, targetLocale } = getActiveLocales();
  return detectUILanguagePreferenceFromOS(sourceLocale, targetLocale) ?? 'target';
}

/**
 * Set UI language preference and apply it.
 */
export async function setUILanguage(pref: UILanguagePreference): Promise<void> {
  await updateLocalPreferences({ uiLanguage: pref });

  const { sourceLocale, targetLocale } = getActiveLocales();

  const i18nLang = resolveUILanguage(pref, sourceLocale, targetLocale);
  await i18n.changeLanguage(i18nLang);
  log.info('UI language changed', { pref, i18nLang });
}

// ─── i18n Initialization ─────────────────────────────────────────

// Initialize i18n synchronously with English default.
// Actual language is set async in initI18n().
i18n.use(initReactI18next).init({
  // Resources keyed by variant — no bare base language keys.
  // Resource keys must match locale codes used by the app.
  resources: {
    'en-US': { translation: enUS },
    'es-419': { translation: es419 },
    'es-MX': { translation: esMX },
    'es-ES': { translation: esES },
    'en-GB': { translation: enGB },
  },
  lng: DEFAULT_UI_LANGUAGE,
  fallbackLng: buildI18nFallbacks(['en-US', 'es-419', 'es-MX', 'es-ES', 'en-GB']),
  interpolation: {
    escapeValue: false,
  },
  react: {
    useSuspense: false,
  },
});

/**
 * Resolve the UI language for app startup (LOCALE-003):
 * 1. Stored explicit choice ('source'/'target') — resolved against the
 *    active course locales.
 * 2. No stored choice: follow the OS language (LOCALE-003-01) — 'source'
 *    or 'target' depending on which course side the OS speaks.
 * 3. OS language matches neither side: DEFAULT_UI_LANGUAGE (LOCALE-003-02).
 */
async function resolveInitialUILanguage(): Promise<string> {
  if (!isLocalPreferencesLoaded()) {
    await loadLocalPreferences();
  }
  const { sourceLocale, targetLocale } = getActiveLocales();

  const stored = getLocalPreferences().uiLanguage;
  const pref =
    stored === 'source' || stored === 'target'
      ? stored
      : detectUILanguagePreferenceFromOS(sourceLocale, targetLocale);
  if (!pref) return DEFAULT_UI_LANGUAGE;
  return resolveUILanguage(pref, sourceLocale, targetLocale);
}

/**
 * Async initialization: detect stored/OS language preference and apply it.
 * Call once at app startup (in App.tsx useEffect).
 */
export async function initI18n(): Promise<void> {
  // Polyfill Intl.DisplayNames for Hermes
  try {
    const { shouldPolyfill: shouldPolyfillGCL } = await import('@formatjs/intl-getcanonicallocales/should-polyfill.js');
    if (shouldPolyfillGCL()) {
      await import('@formatjs/intl-getcanonicallocales/polyfill-force.js');
    }
    const { shouldPolyfill: shouldPolyfillLocale } = await import('@formatjs/intl-locale/should-polyfill.js');
    if (shouldPolyfillLocale()) {
      await import('@formatjs/intl-locale/polyfill-force.js');
    }
    const { shouldPolyfill: shouldPolyfillDN } = await import('@formatjs/intl-displaynames/should-polyfill.js');
    if (shouldPolyfillDN()) {
      await import('@formatjs/intl-displaynames/polyfill-force.js');
      await import('@formatjs/intl-displaynames/locale-data/en.js');
      await import('@formatjs/intl-displaynames/locale-data/es.js');
    }
  } catch (err) {
    log.warn('Failed to load Intl.DisplayNames polyfill', { error: err });
  }

  const i18nLang = await resolveInitialUILanguage();
  if (i18n.language !== i18nLang) {
    await i18n.changeLanguage(i18nLang);
    log.info('i18n initialized', { i18nLang });
  }
}

export default i18n;
